import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCartProductsListBlockConfig } from '@front-platform/content-builder';
import { kioskCartProductsListBlockResolveConfig } from '@front-platform/content-builder';
import { Component, Inject, OnDestroy, OnInit, inject, input } from '@angular/core';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import {
  CartItemKeys,
  ReconstructedCartItemKeys,
  ReconstructedCartKeys,
} from '@config/keys/cart.keys';
import { ProductKeys } from '@config/keys/product.keys';
import { ShopKeys } from '@config/keys/shop.keys';
import {
  CartItemWithOrigin,
  DoodReconstructedCart,
  DoodReconstructedCartItem,
  ICartDeclinableAdditionsItem,
} from '@core/models/cart.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { CartService } from '@core/services/cart/cart.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import {
  ItemEditModalComponent,
  ItemEditModalConfig,
} from '@shared/modals/item-edit-modal/item-edit-modal.component';
import { debounceTime, map, Observable, Subject, switchMap, take, takeUntil } from 'rxjs';
import {
  KIOSK_CART_PRODUCTS_LIST_BLOCK_DATA_SOURCE,
  KioskCartProductsListBlockDataSource,
} from './kiosk-cart-products-list-block-view-model';
import { provideKioskCartProductsListBlockDataSource } from './kiosk-cart-products-list-block-data-source.providers';
export interface KioskCartProductsListBlockRuntimeConfig {}

export type KioskCartProductsListBlockConfigInput = Partial<
  Omit<KioskCartProductsListBlockConfig, keyof KioskCartProductsListBlockRuntimeConfig> &
    KioskCartProductsListBlockRuntimeConfig
>;
export type KioskCartProductsListBlockResolvedConfig = Omit<
  KioskCartProductsListBlockConfig,
  keyof KioskCartProductsListBlockRuntimeConfig
> &
  Partial<KioskCartProductsListBlockRuntimeConfig>;
export interface KioskCartProductsListBlockContract extends ConfigComponentContract<
  KioskCartProductsListBlockConfigInput,
  KioskCartProductsListBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-cart-products-list-block',
  templateUrl: './kiosk-cart-products-list-block.component.html',
  standalone: false,
  providers: [...provideKioskCartProductsListBlockDataSource()],
})
export class KioskCartProductsListBlockComponent
  implements OnInit, OnDestroy, KioskCartProductsListBlockContract
{
  readonly config = input<KioskCartProductsListBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCartProductsListBlockConfig,
    KioskCartProductsListBlockRuntimeConfig
  >(kioskCartProductsListBlockResolveConfig, () => ({ config: this.config() }));
  get title(): string | undefined {
    return this.resolvedConfig()['title'] as unknown as string | undefined;
  }
  get primaryButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['primary_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get primaryButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['primary_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get secondaryButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['secondary_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get secondaryButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['secondary_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get textColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor | undefined;
  }
  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }

  private readonly previewState = inject(Cb2PreviewStateService);
  private _destroyerRef = new Subject<boolean>();

  reconstructedCart$!: Observable<DoodReconstructedCart[] | undefined>;
  reconstructedShareOrderBasket$!: Observable<unknown[]>;
  cartProducts$!: Observable<CartItemWithOrigin[]>;
  cartItemKeys = CartItemKeys;
  productKeys = ProductKeys;
  reconstructedCartKeys = ReconstructedCartKeys;
  reconstructedCartItemKeys = ReconstructedCartItemKeys;
  shopKeys = ShopKeys;
  manageProductQuantity$ = new Subject<{ quantity: number; item: any }>();
  itemBeingUpdated: string[] = [];

  allproducts?: DoodProductModel[];

  currency$!: Observable<string>;

  constructor(
    @Inject(KIOSK_CART_PRODUCTS_LIST_BLOCK_DATA_SOURCE)
    blockDataSource: KioskCartProductsListBlockDataSource,
    private readonly cartService: CartService,
    private readonly modalsService: ModalsService<ItemEditModalConfig>,
    private readonly cartDispatcher: CartStoreDispatcher,
    private readonly orderDispatcher: OrderStoreDispatcher,
  ) {
    const vm$ = blockDataSource.vm$;

    this.reconstructedCart$ = vm$.pipe(map(vm => vm.reconstructedCart));
    this.reconstructedShareOrderBasket$ = vm$.pipe(map(vm => vm.reconstructedShareOrderBasket));
    this.cartProducts$ = vm$.pipe(map(vm => vm.cartProducts));
    this.currency$ = vm$.pipe(map(vm => vm.currency));

    vm$.pipe(takeUntil(this._destroyerRef)).subscribe(vm => {
      this.allproducts = vm.allProducts;
    });
  }

  ngOnInit() {
    if (this.previewState.isActive()) {
      return;
    }

    this.manageProductQuantity$
      .pipe(
        debounceTime(600),
        switchMap(({ quantity, item }) => {
          this.itemBeingUpdated = this.itemBeingUpdated.filter(itemId => itemId != item.id);
          if (quantity > 0) {
            return this.cartService.updateCartItem$({ ...item, quantity });
          } else {
            return this.cartService.removeItem$(item.id as number);
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
  }

  copyCart(cart: any) {
    return { ...cart };
  }

  findProductById(products: DoodReconstructedCart, productId: string) {
    return products.shop.products.find(product => product.id === productId)?.name;
  }

  findByIdFromProducts(productId: string): DoodProductModel | undefined {
    return this.allproducts?.find(product => product.id === productId);
  }

  changeNbProduct(quantity: number, item: any): void {
    this.itemBeingUpdated = [];
    this.itemBeingUpdated.push(item.id);
    this.manageProductQuantity$.next({ quantity, item });
  }

  isBeingUpdated(itemId: string | number) {
    return this.itemBeingUpdated.includes(`${itemId}`);
  }

  onUpdateItem(itemId: number | string): void {
    this.modalsService.open(ItemEditModalComponent.handle, undefined, {
      backgroundColor: this.backgroundColor,
      textColor: this.textColor,
      buttonBackgroundColor: this.primaryButtonBackgroundColor,
      buttonTextColor: this.primaryButtonTextColor,
    });
    this.cartService.loadShopsOfCart$().pipe(takeUntil(this._destroyerRef)).subscribe();
    this.cartService
      .getCartLineItems$()
      .pipe(
        takeUntil(this._destroyerRef),
        take(1),
        map(reconstructedCart => this.lookForItemInCart(itemId, reconstructedCart)),
        map(cartItem => {
          if (cartItem) {
            this.cartDispatcher.updateStore({ edit_item: cartItem });
            this.orderDispatcher.checkCartIsValid();
          }
        }),
      )
      .subscribe();
  }

  private lookForItemInCart(
    itemId: number | string,
    reconstructedCart: DoodReconstructedCart[],
  ): null | DoodReconstructedCartItem {
    for (const cartOfShop of reconstructedCart) {
      const cartItem = cartOfShop[ReconstructedCartKeys.Items].find(
        elem => elem[ReconstructedCartItemKeys.Item][CartItemKeys.Id] === itemId,
      );
      if (cartItem) {
        return cartItem;
      }
    }

    return null;
  }

  protected mergeCartProductQuantities = (items: ICartDeclinableAdditionsItem[] | undefined) => {
    if (!items) {
      return [];
    }
    const result = items.reduce((acc, item) => {
      const existingItem = acc.find(i => i.id === item.id);
      if (existingItem) {
        existingItem.quantity += item.quantity;
      } else {
        acc.push({ ...item });
      }
      return acc;
    }, [] as ICartDeclinableAdditionsItem[]);
    return result;
  };
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCartProductsListBlockComponent, {
  'keyAliases': {},
});
